import { useState } from 'react'
import { X, Check, Plane } from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { useAircraftGetAll } from '../lib/hooks'
import { ListSkeleton } from './ui/skeleton-presets'
import { SearchInput } from './ui/search-input'
import { cn } from '@/lib/utils'
import { EmptyState } from './EmptyState'
import { AircraftDrawer } from './AircraftDrawer'
import { AircraftScheduleCard } from './AircraftScheduleCard'

interface AircraftSelectionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // null = All Aircraft (no filter)
  selectedAircraftId: string | null
  onSelect: (aircraftId: string | null) => void
}

export function AircraftSelectionDrawer({
  open,
  onOpenChange,
  selectedAircraftId,
  onSelect,
}: AircraftSelectionDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddAircraftOpen, setIsAddAircraftOpen] = useState(false)

  const { data: allAircraft = [], isPending: loading } = useAircraftGetAll()

  const filteredAircraft = allAircraft.filter((plane) =>
    (plane.registration || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (plane.type || '').toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleClose = () => {
    setSearchQuery('')
    onOpenChange(false)
  }

  // Selecting an aircraft (or "All Aircraft") applies immediately and closes.
  const handleSelect = (aircraftId: string | null) => {
    onSelect(aircraftId)
    handleClose()
  }

  return (
    <>
      <Drawer open={open} onOpenChange={(o) => !o && handleClose()}>
        <DrawerContent className="bg-surface-L0 rounded-t-[10px] max-h-[85dvh]">
          <DrawerHeader className="flex items-center justify-between p-4 border-b border-border-L2">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Select Aircraft
            </DrawerTitle>
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Content */}
          <div className="flex-1 overflow-hidden px-4 pt-4">
            <div className="flex flex-col gap-2 h-full">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tail number"
              />

              <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
                {loading ? (
                  <ListSkeleton />
                ) : allAircraft.length === 0 ? (
                  <EmptyState
                    icon={Plane}
                    title="No Aircraft"
                    description="Add an aircraft to filter the schedule by it."
                    addButtonText="Add Aircraft"
                    onAdd={() => setIsAddAircraftOpen(true)}
                  />
                ) : (
                  <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
                    {/* All Aircraft option — clears the selection */}
                    {!searchQuery && (
                      <button
                        onClick={() => handleSelect(null)}
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border transition-colors',
                          selectedAircraftId === null
                            ? 'bg-surface-L1 border-border-L2'
                            : 'bg-surface-L0 border-border-L1 active:bg-surface-L1'
                        )}
                      >
                        <AircraftScheduleCard
                          aircraft={null}
                          totalCount={allAircraft.length}
                          trailing={
                            selectedAircraftId === null ? (
                              <Check className="size-5 text-content-primary-bold" />
                            ) : null
                          }
                        />
                      </button>
                    )}

                    {filteredAircraft.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 px-4">
                        <p className="text-sm text-content-primary-subtle text-center">
                          No aircraft found matching your search
                        </p>
                      </div>
                    ) : (
                      filteredAircraft.map((plane) => {
                        const isSelected = selectedAircraftId === plane.id

                        return (
                          <button
                            key={plane.id}
                            onClick={() => handleSelect(plane.id)}
                            className={cn(
                              'w-full px-3 py-2 rounded-lg border transition-colors',
                              isSelected
                                ? 'bg-surface-L1 border-border-L2'
                                : 'bg-surface-L0 border-border-L1 active:bg-surface-L1'
                            )}
                          >
                            <AircraftScheduleCard
                              aircraft={plane}
                              trailing={
                                isSelected ? (
                                  <Check className="size-5 text-content-primary-bold" />
                                ) : null
                              }
                            />
                          </button>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Add Aircraft Drawer — opens from the empty state */}
      <AircraftDrawer
        isOpen={isAddAircraftOpen}
        onClose={() => setIsAddAircraftOpen(false)}
      />
    </>
  )
}
